/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import {
  SignatureRequest,
  SignerContextShape,
} from 'signer-app/signer-signature-document/types';

import * as Sentry from '@sentry/browser';
import {
  Field,
  type CheckboxField,
  type RadioField,
} from 'signer-app/types/editor-types';
import { validationRegex } from 'signer-app/signer-experience/signer-validation-constants';
import { useFeatureFlag } from 'signer-app/context/feature-flags';
import invariant from 'invariant';
import { GROUP_REQUIREMENT_PATTERN_STRICT } from 'js/sign-components/common/group-requirement';
import { atom, useAtomValue, useSetAtom } from 'jotai';

function logOvertimeValidation(regex: RegExp, startTime: number) {
  const logTimeout = 2000; // When more than 2s we capture a log
  const now = Date.now();
  if (now > startTime + logTimeout) {
    Sentry.captureMessage(`Regex ${regex} took more than ${logTimeout}ms`);
  }
}

const messages = defineMessages({
  numbers_only: {
    id: 'error.validation.numbers_only',
    description:
      'error message for input field in signer flow, shows when inputted data in not numbers only',
    defaultMessage: 'This field only accepts numbers',
  },
  letters_only: {
    id: 'error.validation.letters_only',
    description:
      'error message for input field in signer flow, shows when inputted data in not letters only',
    defaultMessage: 'This field only accepts letters',
  },
  phone_number: {
    id: 'error.validation.phone_number',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid phone number',
    defaultMessage: 'Please enter a 10- or 11-digit telephone number',
  },
  bank_routing_number: {
    id: 'error.validation.bank_routing_number',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid bank routing number',
    defaultMessage: 'Please enter a 9-digit routing number',
  },
  bank_account_number: {
    id: 'error.validation.bank_account_number',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid account number',
    defaultMessage: 'Please enter a minimum of 6 digits',
  },
  email_address: {
    id: 'error.validation.email_address',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid email address',
    defaultMessage: 'Please enter a valid email address',
  },
  zip_code: {
    id: 'error.validation.zip_code',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid zip code',
    defaultMessage: 'Please enter a 5- or 9-digit zip code',
  },
  social_security_number: {
    id: 'error.validation.social_security_number',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid social security number',
    defaultMessage: 'Please enter a 9-digit social security number',
  },
  employer_identification_number: {
    id: 'error.validation.employer_identification_number',
    description:
      'error message for input field in signer flow, shows when inputted data is not valid employee ID number',
    defaultMessage: 'Please enter a 9-digit employer identification number',
  },
  custom_regex: {
    id: 'error.validation.custom_regex',
    description:
      'error message for input field in signer flow, shows when inputted data does not pass regex validation',
    defaultMessage: 'The field is invalid.',
  },
  custom_regex_with_format: {
    id: 'error.validation.custom_regex_with_format',
    description:
      'error message for input field in signer flow, shows when inputted data does not pass regex validation',
    defaultMessage: 'The field is invalid. Required format: {format}',
  },
  requiredMessage: {
    id: 'error.field.requiredMessage',
    description: 'generic error message when required field is not filled',
    defaultMessage: 'This field is required',
  },
  fieldsMissing: {
    id: 'error.field.fieldsMissing',
    description: 'generic error message when required field is not filled',
    defaultMessage: "Required fields are missing. Please click 'Edit'.",
  },
  minNotMetMessage: {
    id: 'error.field.minNotMetMessage',
    description:
      'generic error message when minimum required boxes are not selected',
    defaultMessage:
      'Please select at least {min} { min, plural, =1 {item} other {items} }',
  },
  maxExceededMessage: {
    id: 'error.field.maxExceededMessage',
    description:
      'generic error message when more then the limit of boxes are selectex',
    defaultMessage: 'You have selected too many boxes. Please uncheck one.',
  },
  generalErrorMessage: {
    id: 'error.general.message',
    description: 'general error message',
    defaultMessage: 'An error occurred',
  },
  minOptionsValidationInfo: {
    id: 'c076144cd29a1633e5c5809bce16c1f631f5fff1a07fa0601a2663595d69ba37',
    description:
      'Validation info text for minimum number of options that should be selected for a group of checkboxes',
    defaultMessage:
      'Select min. {min} { min, plural, =1 {option} other {options} }.',
  },
  maxOptionsValidationInfo: {
    id: '3c8dfe313ac86d023beedb69b016307237f771114a5c6dac7fd0ce9e54b006a5',
    description:
      'Validation info text for maximum number of options that can be selected for a group of checkboxes',
    defaultMessage:
      'Select max. {max} { max, plural, =1 {option} other {options} }.',
  },
  exactOptionsValidationInfo: {
    id: 'a4539d03f9cfea1414cee12f182106522fb01e4b170d55433a9c913ece7d2bde',
    description:
      'Validation info text for the exact number of options that should be selected for a group of checkboxes',
    defaultMessage:
      'Select {max} { max, plural, =1 {option} other {options} }.',
  },
  rangeOptionsValidationInfo: {
    id: 'd900461803cf1057475458d89d4ca8ba7bf4f39dd8b231074224fba0d7eb9b48',
    description:
      'Validation info text for the range of options that can be selected for a group of checkboxes',
    defaultMessage: 'Select between {min} and {max} options.',
  },
});

const touchedFieldIdsAtom = atom(
  new Set<string>(),
  (get, set, fieldId: string) => {
    const touchedFields = get(touchedFieldIdsAtom);
    if (!touchedFields.has(fieldId)) {
      const newSet = new Set(touchedFields);
      newSet.add(fieldId);
      // @ts-expect-error when calling the set inside the same atom, it will NOT
      // call this function recursively. It sets the value, but the TypeScript types
      // don't seem to have a good way to represent that.
      set(touchedFieldIdsAtom, newSet);
    }
  },
);

/**
 * Returns a function to mark a field as touched
 */
export function useTouchField() {
  const setTouchedFieldsIds = useSetAtom(touchedFieldIdsAtom);

  /**
   * I could return setTouchedFieldsIds directly, but I think having it makes it more
   * clear that we're passing in a fieldId and not a (prev) => ({...prev, ...})
   * setter
   */
  return function touchField(fieldId: string) {
    setTouchedFieldsIds(fieldId);
  };
}

/**
 * Returns all touched field ids
 */
export function useTouchedFieldIds() {
  return useAtomValue(touchedFieldIdsAtom);
}

/**
 * Returns true if the field is touched
 */
export function useIsTouched(fieldId: string) {
  const touchedFields = useTouchedFieldIds();
  return touchedFields.has(fieldId);
}

/**
 * Returns the validation errors for all fields in the request.
 *
 * Malformed group requirements (corrupted data from the editor regex bug
 * SIGN-14073) are collected by validateGroups and logged here once per
 * unique (signatureRequestId, group) pair. Logging is gated by the
 * sign_core_2026_03_05_log_malformed_group_requirement flag so it can be
 * silenced from GrowthBook without a redeploy.
 */
export function useValidation(
  signatureRequest: SignatureRequest | undefined,
  signatureRequestId: string | undefined,
): SignerContextShape['validationErrors'] {
  const intl = useIntl();
  const fixInvariant = useFeatureFlag(
    'sign_core_2026_03_03_fix_group_requirement_invariant',
  );
  const logMalformed = useFeatureFlag(
    'sign_core_2026_03_05_log_malformed_group_requirement',
  );

  // Track which malformed groups we've already reported for this request
  // so each unique issue is logged exactly once per signature request.
  const loggedRef = React.useRef<{
    id: string | undefined;
    logged: Set<string>;
  }>({ id: undefined, logged: new Set() });
  if (loggedRef.current.id !== signatureRequestId) {
    loggedRef.current = { id: signatureRequestId, logged: new Set() };
  }

  const fields = signatureRequest?.fields ?? [];
  const fieldErrors = fields.reduce<SignerContextShape['validationErrors']>(
    (errors, field) => {
      const result = validateField(field, intl);
      if (result) {
        errors[result.id] = { message: result.message };
      }

      return errors;
    },
    {},
  );

  const { errors: groupErrors, malformedGroups } = validateGroups(
    fields,
    intl,
    fixInvariant,
  );

  if (logMalformed) {
    for (const { group, requirement, reason } of malformedGroups) {
      const key = `${group}:${requirement}`;
      if (!loggedRef.current.logged.has(key)) {
        loggedRef.current.logged.add(key);
        Sentry.captureMessage('validateGroups: malformed group requirement', {
          level: 'warning',
          tags: {
            malformedGroupReason: reason,
          },
          contexts: {
            malformedGroup: {
              group,
              requirement: JSON.stringify(requirement),
              signatureRequestId: signatureRequestId ?? 'unknown',
              reason,
            },
          },
        });
      }
    }
  }

  return {
    ...fieldErrors,
    ...groupErrors,
  };
}

/**
 * Pure parser: extracts min/max from a requirement string like "require_1-2".
 * Returns null when fixInvariant is on and the string doesn't match — callers
 * are responsible for logging. When fixInvariant is off, throws via invariant
 * (legacy behavior).
 */
function parseGroupRequirement(
  requirement: string,
  fixInvariant = false,
): { min: string; max: string } | null {
  const pattern = fixInvariant
    ? GROUP_REQUIREMENT_PATTERN_STRICT
    : /require_(\d+)-?(\d+|ormore)?/;
  const match = requirement.match(pattern);
  if (!match) {
    if (fixInvariant) {
      return null;
    }
    invariant(
      match,
      `Requirement should match the pattern. Found ${requirement}`,
    );
  }
  const [, min, max = min] = match!;
  return { min, max };
}

/**
 * Note that this method is only for checkbox groups.
 */
export function getGroupValidationInfo(
  requirement: string,
  intl: IntlShape,
  fixInvariant = false,
) {
  if (!requirement) {
    return;
  }

  const parsed = parseGroupRequirement(requirement, fixInvariant);
  if (!parsed) {
    return;
  }
  const { min, max } = parsed;

  if (Number(min) === 0) {
    if (max === 'ormore') {
      return;
    } else {
      return intl.formatMessage(messages.maxOptionsValidationInfo, { max });
    }
  }

  if (max === 'ormore') {
    return intl.formatMessage(messages.minOptionsValidationInfo, { min });
  }

  if (Number(min) === Number(max)) {
    return intl.formatMessage(messages.exactOptionsValidationInfo, {
      max,
    });
  }

  return intl.formatMessage(messages.rangeOptionsValidationInfo, {
    min,
    max,
  });
}

export function isGroupRequired(requirement: string, fixInvariant = false) {
  if (!requirement) {
    return false;
  }

  const parsed = parseGroupRequirement(requirement, fixInvariant);
  if (!parsed) {
    return false;
  }

  return Number(parsed.min) !== 0;
}

export function validateField(
  field: Field,
  intl: IntlShape,
): null | { id: string; message: string } {
  switch (field.type) {
    case 'date':
      // Date doesn't have validation, users don't get to interact with it.
      break;
    case 'checkbox': {
      if (field.required && !field.checked) {
        return {
          id: field.id,
          message: intl.formatMessage(messages.requiredMessage),
        };
      }
      break;
    }
    case 'initials':
    case 'signature': {
      if (field.required && !field.signature) {
        return {
          id: field.id,
          message: intl.formatMessage(messages.requiredMessage),
        };
      }
      break;
    }
    case 'dropdown': {
      if (field.required && !field.value) {
        return {
          id: field.id,
          message: intl.formatMessage(messages.requiredMessage),
        };
      }

      break;
    }
    case 'text': {
      if (
        field.required &&
        (field.value || '').replace(/[\n\r]/g, '').length < 1
      ) {
        return {
          id: field.id,
          message: intl.formatMessage(messages.requiredMessage),
        };
      }

      let regex: RegExp | undefined;
      switch (field.validationType) {
        case 'numbers_only':
        case 'letters_only':
        case 'phone_number':
        case 'bank_routing_number':
        case 'bank_account_number':
        case 'email_address':
        case 'zip_code':
        case 'social_security_number':
        case 'employer_identification_number': {
          invariant(
            validationRegex[field.validationType],
            'Unable to locate regex for validation',
          );
          regex = validationRegex[field.validationType];

          break;
        }
        case 'custom_regex':
          if (field.validationCustomRegex != null) {
            try {
              regex = new RegExp(field.validationCustomRegex);
            } catch (e) {
              // TypeScript knows that e is unknown, anything can be thrown
              // from anywhere. But I've wrapped this round a native API and
              // I'm just going to trust that I'll get an Error here.
              const error = e as Error;
              Sentry.captureMessage(
                `Error while initializing new custom regex ${error.message}`,
              );
              throw error;
            }
          }
          break;
        default:
          break;
      }

      if (field.validationType && regex) {
        const startRegexTestTime = Date.now();
        const textValue = field.value?.trim().replace(/\n/g, '') ?? '';
        // Empty optional fields cannot fail validation
        if (!field.required && !textValue) {
          return null;
        }
        if (!regex.test(textValue)) {
          if (
            field.validationType === 'custom_regex' &&
            field.validationCustomRegexFormatLabel
          ) {
            return {
              id: field.id,
              message: intl.formatMessage(messages.custom_regex_with_format, {
                format: field.validationCustomRegexFormatLabel,
              }),
            };
          } else if (field.validationType) {
            return {
              id: field.id,
              message: intl.formatMessage(messages[field.validationType]),
            };
          }
        }
        logOvertimeValidation(regex, startRegexTestTime);
      }
      break;
    }
    default:
  }
  return null;
}

export type MalformedGroup = {
  group: string;
  requirement: unknown;
  reason: 'not_a_string' | 'inconsistent' | 'pattern_mismatch';
};

type GroupValidationResult = {
  errors: SignerContextShape['validationErrors'];
  malformedGroups: MalformedGroup[];
};

/**
 * Validates all checkbox/radio groups and returns both validation errors
 * and a list of malformed groups encountered. Malformed groups are collected
 * (not logged) so the caller can deduplicate and gate telemetry.
 */
export function validateGroups(
  fields: Field[],
  intl: IntlShape,
  fixInvariant = false,
): GroupValidationResult {
  const malformedGroups: MalformedGroup[] = [];
  const groupErrors: SignerContextShape['validationErrors'] = {};
  const groupedFields = fields.reduce(
    (groups, field) => {
      if ('group' in field && field.group) {
        if (field.type === 'checkbox' || field.type === 'radiobutton') {
          groups[field.group] ??= [];
          groups[field.group].push(field);
        }
      }
      return groups;
    },
    {} as Record<string, Array<CheckboxField | RadioField>>,
  );

  const errors = Object.entries(groupedFields).reduce(
    (errors, [group, groupFields]) => {
      const firstField = groupFields[0];
      const { requirement } = firstField;
      if (typeof requirement !== 'string') {
        if (fixInvariant) {
          malformedGroups.push({
            group,
            requirement,
            reason: 'not_a_string',
          });
          return errors;
        }
        invariant(false, 'Requirement should be a string');
      }
      if (
        !groupFields.every(
          (field) => field.group === group && field.requirement === requirement,
        )
      ) {
        if (fixInvariant) {
          malformedGroups.push({
            group,
            requirement,
            reason: 'inconsistent',
          });
          return errors;
        }
        invariant(
          false,
          'All fields in a group should have the same group and requirement',
        );
      }
      const parsed = parseGroupRequirement(requirement, fixInvariant);
      if (!parsed) {
        if (fixInvariant) {
          malformedGroups.push({
            group,
            requirement,
            reason: 'pattern_mismatch',
          });
        }
        return errors;
      }
      const { min, max } = parsed;

      const checkedFields = groupFields.filter((field) => field.checked).length;
      if (Number(min) > checkedFields) {
        groupFields.forEach((field) => {
          errors[field.id] = {
            message: intl.formatMessage(messages.minNotMetMessage, { min }),
          };
        });
      }
      if (Number(max) > 0 && Number(max) < checkedFields) {
        groupFields.forEach((field) => {
          errors[field.id] = {
            message: intl.formatMessage(messages.maxExceededMessage),
          };
        });
      }

      return errors;
    },
    groupErrors,
  );

  return { errors, malformedGroups };
}
